import React from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PURCHASE_STATUS, publishDisabledReason } from '../../utils/purchasePublication';

const statusLabels = { 0: 'DELETED', 1: 'ACTIVE', 2: 'EXPIRED', 3: 'DEACTIVATED', 4: 'PUBLISHED' };

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
    });
}

export default function PurchaseList({ purchases = [], companyName, allPurchases = purchases, onPublicationChange, pendingId, centralCompanyId }) {
    const navigate = useNavigate();
    if (!purchases.length) return <p className="purchase-empty">No purchases found for this company.</p>;

    return (
      <div className="purchase-table-scroll" tabIndex={0} role="region" aria-label={`Purchase records for ${companyName}`}>
        <table className="purchase-table">
          <caption>Purchases for {companyName}</caption>
          <thead>
            <tr>
              <th scope="col">Quote line / Item</th>
              <th scope="col">Portal</th>
              <th scope="col">Status</th>
              <th scope="col">Start date (UTC)</th>
              <th scope="col">End date (UTC)</th>
              <th scope="col">Premium</th>
              <th scope="col">Featured</th>
              <th scope="col">Active stories</th>
              <th scope="col">Last story published (UTC)</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map(purchase => (
              <tr key={purchase._id}>
                <td>
                  <strong>{purchase.quotelineNo}</strong>
                  <span className="purchase-item-description">{purchase.itemDescription}</span>
                  <span className="purchase-item-code">{purchase.itemCode}</span>
                </td>
                <td><span className="purchase-portal">{purchase.origin}</span></td>
                <td><span className={`purchase-status purchase-status-${purchase.status}`}>
                  {statusLabels[ purchase.status ] || purchase.statusLabel || 'Unknown'}
                </span></td>
                <td>{formatDate(purchase.startDate)}</td>
                <td>{formatDate(purchase.endDate)}</td>
                <td><span className={`purchase-flag${purchase.isPremium ? ' is-enabled' : ''}`}>{purchase.isPremium ? 'Yes' : 'No'}</span></td>
                <td><span className={`purchase-flag${purchase.featured ? ' is-enabled' : ''}`}>{purchase.featured ? 'Yes' : 'No'}</span></td>
                <td><span className="purchase-story-count">{purchase.activeStoryCount ?? 0}</span></td>
                <td>{formatDate(purchase.storyUpdatedOn)}</td>
                <td>
                  <div className="purchase-row-actions">
                    <button type="button" onClick={() => navigate(`/companies/${centralCompanyId}/purchases/${purchase._id}/stories`)}>View stories</button>
                    {purchase.status === PURCHASE_STATUS.PUBLISHED && <button type="button" disabled={Boolean(pendingId)} onClick={() => navigate(`/companies/${centralCompanyId}/purchases/${purchase._id}/stories/add`, { state: { purchaseId: purchase._id, companyName } })}>Add story</button>}
                    {purchase.status === PURCHASE_STATUS.PUBLISHED && <button type="button" disabled={Boolean(pendingId)} onClick={() => navigate(`/companies/${centralCompanyId}/purchases/${purchase._id}/stories/import`, { state: { purchase } })}>Import stories</button>}
                    {onPublicationChange && (purchase.status === PURCHASE_STATUS.PUBLISHED
                        ? <button className="purchase-unpublish" type="button" disabled={Boolean(pendingId)} onClick={() => onPublicationChange(purchase, 'unpublish')}>
                          {pendingId === purchase._id ? 'Unpublishing…' : 'Unpublish'}
                        </button>
                        : <button type="button" disabled={Boolean(pendingId) || Boolean(publishDisabledReason(purchase, allPurchases))}
                            title={publishDisabledReason(purchase, allPurchases)} onClick={() => onPublicationChange(purchase, 'publish')}>
                          {pendingId === purchase._id ? 'Publishing…' : 'Publish'}
                        </button>)}
                  </div>
                </td>
              </tr>
                    ))}
          </tbody>
        </table>
      </div>
    );
}

PurchaseList.propTypes = {
    purchases: PropTypes.arrayOf(PropTypes.object),
    companyName: PropTypes.string.isRequired,
    allPurchases: PropTypes.arrayOf(PropTypes.object),
    onPublicationChange: PropTypes.func,
    pendingId: PropTypes.string,
    centralCompanyId: PropTypes.string.isRequired,
};
